/** RecordingTranscriptEditor — edit a recording's transcript.
 *
 *  Two modes:
 *  - ASR pending: read-only textarea showing streaming text + a "重新转写"
 *    button to re-trigger ASR.
 *  - Otherwise: editable textarea backed by POST /meetings/{id}/transcript
 *    (manual edits).
 *
 *  Phase-2 additions: ``pending`` prop drives the read-only streaming mode;
 *  ``onRetrigger`` callback lets the parent fire ASR (parent owns the trigger
 *  mutation so it can also invalidate the meeting list).
 */

import { useEffect, useState } from "react";
import { Button, App } from "antd";
import { SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import { useImportMeetingTranscript } from "../../api/queries";
import styles from "../../styles/recording.module.css";

interface RecordingTranscriptEditorProps {
  meetingId: string;
  /** Latest transcript text (live-updates while ASR is pending). */
  initialText: string | null;
  /** When true, the textarea is read-only and shows streaming ASR output. */
  pending?: boolean;
  /** When provided, shows a "重新转写" button that calls this handler. */
  onRetrigger?: () => void;
  /** Close handler — for the parent Drawer / collapse. */
  onClose?: () => void;
}

export default function RecordingTranscriptEditor({
  meetingId,
  initialText,
  pending = false,
  onRetrigger,
  onClose,
}: RecordingTranscriptEditorProps) {
  const { message } = App.useApp();
  const [text, setText] = useState(initialText ?? "");
  const importMutation = useImportMeetingTranscript();

  // Resync if the parent swaps to a different meeting or the server value
  // changes after a successful save / ASR incremental update.
  useEffect(() => {
    setText(initialText ?? "");
  }, [meetingId, initialText]);

  const handleSave = async () => {
    try {
      await importMutation.mutateAsync({
        id: meetingId,
        data: { text },
      });
      message.success("文字稿已保存");
      onClose?.();
    } catch (err) {
      message.error(`保存失败：${(err as Error)?.message ?? "请稍后重试"}`);
    }
  };

  const handleRetrigger = () => {
    if (pending) return;  // can't retrigger while one is already running
    onRetrigger?.();
  };

  const dirty = text !== (initialText ?? "");

  return (
    <div className={styles.transcriptBox}>
      <div className={styles.transcriptHeader}>
        <span className={styles.transcriptTitle}>文字稿</span>
        <span className={styles.transcriptHint}>
          {pending ? "ASR 正在转写，文字将持续更新…" : "可手动编辑，或点重新转写让 ASR 重跑"}
        </span>
      </div>
      <textarea
        className={styles.transcriptTextarea}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={pending ? "等待转写结果…" : "在此撰写会议纪要、客户谈话要点、证言记录…"}
        rows={10}
        readOnly={pending}
      />
      <div className={styles.transcriptActions}>
        <span className={styles.transcriptCounter}>{text.length} 字</span>
        <div className={styles.transcriptButtons}>
          {onRetrigger && (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleRetrigger}
              disabled={pending}
              loading={pending}
            >
              重新转写
            </Button>
          )}
          {onClose && (
            <Button size="small" onClick={onClose} disabled={importMutation.isPending || pending}>
              取消
            </Button>
          )}
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={importMutation.isPending}
            disabled={!dirty || pending}
          >
            保存
          </Button>
        </div>
      </div>
    </div>
  );
}
